/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Box, Checkbox, CircularProgress, Divider, FormControlLabel, Paper, Typography } from '@mui/material';
import { isEmpty, pickBy } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { allocationsAction, captableAction, companiesAction } from 'common/actions';
import { companySummaryUrl, firmSummaryUrl } from 'common/config/urls';
import { AI_GRADIENT_COLORS, AI_GRADIENT_CSS } from 'common/constants/ai';
import {
  COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE,
  COMPANY_TYPE,
  RELATED_ENTITY,
} from 'common/constants/companies';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { FormDialog } from 'components';
import CommonProfilePicker from 'components/CommonProfilePicker';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { CompanyForm } from 'components/Forms';
import { CompanyFormDefaultFormState } from 'components/Forms/CompanyForm/CompanyForm.props';
import DialogContext from 'context/DialogContext';
import { CompaniesService, FirmService } from 'services';
import { CompanyHk } from 'services/hooks';
import { useConfirmCompanyProfile, useGetCommonProfile, useLinkCompanyToProfile } from 'services/hooks/companies';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useUserUtils } from 'utilities/userUtils';
import { NewCompanyDialogProps } from './NewCompanyDialog.props';
import { useCreateFirmCompany } from '../../../../services/hooks/useCreateFirmCompany';

const {useUpdateCompanyById} = CompanyHk;
const ContentComponent = (itemName: string) => <ConfirmationDelete itemName={itemName}/>;

const NewCompanyDialog = ({
  open,
  onClose,
  editMode = false,
  isRelatedEntity = false,
  onSave
}: NewCompanyDialogProps) => {
  const {data: createdCompany, createCompany} = useCreateFirmCompany();
  const firmService = new FirmService();
  const [{companyInfo, firmId, firmInfo}, dispatch] = useStore();
  const {isStaffUser, isSuperUser} = useUserUtils();
  const showProfilePicker = editMode && !isRelatedEntity && (isStaffUser() || isSuperUser());
  const [pendingProfileId, setPendingProfileId] = useState<number | null | undefined>(
    () => companyInfo.common_profile_id as number | null | undefined
  );
  const showProfileConfirm = editMode && !isRelatedEntity
        && pendingProfileId != null
        && (companyInfo.common_profile_confirmed as boolean | null | undefined) === false;
  const linkMutation = useLinkCompanyToProfile();
  const getCompanyInfo = useGetCompanyInfoById();
  const {data: pendingProfile, isLoading: pendingProfileLoading} = useGetCommonProfile(
    showProfileConfirm ? pendingProfileId as number : null,
  );
  const confirmMutation = useConfirmCompanyProfile();
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const defaultValues = editMode
    ? companyInfo
    : {
      name: '',
      legal_company_name: '',
      website: '',
      fiscal_year_end: '',
      captable_currency: 'USD',
      financials_currency: 'USD',
    };
  const navigate = useNavigate();
  const {firmSlugParam} = useParams() as { firmSlugParam: string };
  const {showDialog} = useContext(DialogContext);
  const [formState, setFormState] = useState({
    isValid: editMode,
    values: defaultValues,
    errors: {},
  });

  const isEmptyCompany = isEmpty(pickBy(formState.values));

  const updateCompany = useUpdateCompanyById();
  const [allowDeleteCompany, setAllowDeleteCompany] = useState(true);

  const deleteCompany = async () => {
    try {
      const companySvc = new CompaniesService();
      await companySvc.deleteCompany(companyInfo.id);
      onClose();
      navigate({pathname: firmSummaryUrl(firmSlugParam)});
      const companies = (await firmService.getCompanyListByFirmId(companyInfo.firm_id)) as FirmCompany[];
      dispatch(companiesAction.setCompanyList(companies));
    } catch (error) {
      return error;
    }
  };

  const handleDeleteCompany = () => {
    showDialog({
      wrapper: ConfirmationDialog,
      content: () => ContentComponent(`Company: ${companyInfo.name}`),
      actions: [
        {
          label: 'Delete',
          variant: 'contained',
          type: 'danger',
          callback: deleteCompany,
        },
      ],
    });
  };

  const saveData = async () => {
    if (editMode) {
      const {
        firm_id,
        name,
        legal_company_name,
        website,
        fiscal_year_end,
        captable_currency,
        financials_currency
      } = formState.values;

      const companyFormValues = {
        firm: firm_id,
        name,
        legal_company_name,
        website,
        fiscal_year_end,
        captable_currency,
        financials_currency,
      };

      // We must await the update so the Dialog knows to show the loading state
      const updateResult = await updateCompany(companyInfo.id, companyFormValues, firmSlugParam);

      // If updateCompany follows the same pattern of returning null/undefined on error,
      // we throw here to prevent the dialog from closing.
      if (!updateResult) {
        throw new Error('The company update failed');
      }

      // Update common profile link if it changed
      if (showProfilePicker) {
        const currentProfileId = companyInfo.common_profile_id as number | null | undefined;
        if (pendingProfileId !== currentProfileId) {
          await linkMutation.mutateAsync({companyId: companyInfo.id, profileId: pendingProfileId ?? null});
          await getCompanyInfo(companyInfo.id as number);
        }
      }

      if (showProfileConfirm && pendingConfirm) {
        await confirmMutation.mutateAsync({companyId: companyInfo.id as number});
        await getCompanyInfo(companyInfo.id as number);
      }
    } else {
      const {values: companyData} = formState;

      const createPayload = isRelatedEntity
        ? {
          ...companyData,
          type: COMPANY_TYPE.HOLDING,
          parent_company: companyInfo.id,
          firm: firmId,
        }
        : {
          ...companyData,
          type: COMPANY_TYPE.PORTFOLIO,
          firm: firmId,
        };

      const newCompany = await createCompany(createPayload);

      // Since the createCompany hook swallows the error and returns null,
      // we manually throw to signal failure to the form dialog.
      if (!newCompany) {
        throw new Error('The company creation failed');
      }

      // For related entities, stay on the current page and let the parent handle refresh
      if (isRelatedEntity && onSave) {
        onSave(newCompany.name);
        // Manual onClose() removed; the form dialog handles this on successful resolution.
      } else if (!isEmpty(firmInfo)) {
        // For regular companies, navigate to the summary page
        dispatch(captableAction.setCaptableInfo({}));
        dispatch(allocationsAction.setAllocationInfo({}));
        navigate(companySummaryUrl(firmInfo.slug, newCompany.slug as string));
      }
    }
  };

  const getTitle = useCallback(() => {
    if (isRelatedEntity) {
      return RELATED_ENTITY.TITLE;
    }
    if (editMode) {
      return companyInfo.name;
    }
    return 'Add Company';
  }, [isRelatedEntity, editMode, companyInfo]);

  useEffect(() => {
    if (!editMode || isEmpty(companyInfo) || companyInfo?.measurement_date_id) {
      setAllowDeleteCompany(false);
    }
  }, [companyInfo]);

  useEffect(() => {
    if (open) {
      setPendingProfileId(companyInfo.common_profile_id as number | null | undefined);
      setPendingConfirm(false);
    }
  }, [open]);

  useEffect(() => {
    if (pendingProfileId == null) {
      setPendingConfirm(false);
    }
  }, [pendingProfileId]);

  useEffect(() => {
    // Skip navigation for related entities - they stay on the current page
    if (isRelatedEntity && onSave) {
      return;
    }
    if (!isEmpty(createdCompany) && !isEmpty(firmInfo)) {
      dispatch(captableAction.setCaptableInfo({}));
      dispatch(allocationsAction.setAllocationInfo({}));
      navigate(companySummaryUrl(firmInfo.slug, createdCompany.slug as string));
    }
  }, [createdCompany, firmInfo, isRelatedEntity, onSave]);

  const shouldCheckFeatureEnabler = useMemo(() => !allowDeleteCompany, [allowDeleteCompany]);

  return (
    <FormDialog
      open={open}
      title={getTitle()}
      editMode={editMode}
      subtitle={isRelatedEntity ? RELATED_ENTITY.SUBTITLE : ''}
      onSave={saveData}
      onClose={onClose}
      onDelete={handleDeleteCompany}
      allowDelete={allowDeleteCompany}
      isValid={formState.isValid}
      confirmCancel={!isEmptyCompany}
      deleteText="Delete Company"
      toolTipText={COMPANIES_TOOLTIP_TEXT_COMPANY_MEASUREMENT_DATE}
      shouldCheckFeatureEnabler={shouldCheckFeatureEnabler}>
      <>
        <CompanyForm
          editMode={editMode}
          initialValues={defaultValues}
          defaultFormState={formState as unknown as CompanyFormDefaultFormState}
          onFormChange={formCurrentState => setFormState(formCurrentState)}
          isRelatedEntity={isRelatedEntity}
        />
        {showProfilePicker && (
          <CommonProfilePicker
            selectedProfileId={pendingProfileId}
            onChange={setPendingProfileId}
            disabled={linkMutation.isPending}
          />
        )}
        {showProfileConfirm && (
          <Box>
            <Divider sx={{my: 2}}/>
            <Typography variant="subtitle2" sx={{mb: 1}}>Common Profile</Typography>
            {pendingProfileLoading ? (
              <CircularProgress size={20}/>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  borderLeft: `3px solid ${AI_GRADIENT_COLORS.purple}`,
                  background: 'linear-gradient(90deg, rgba(166,20,255,0.06) 0%, transparent 100%)',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <AutoAwesomeIcon sx={{
                    fontSize: 16,
                    background: AI_GRADIENT_CSS,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}/>
                  <Typography variant="body2">
                                        AI-suggested match:{' '}
                    <strong>{pendingProfile?.name ?? '—'}</strong>
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={pendingConfirm}
                      onChange={e => setPendingConfirm(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Confirm</Typography>}
                  sx={{mr: 0, ml: 0}}
                />
              </Paper>
            )}
          </Box>
        )}
      </>
    </FormDialog>
  );
};

export default NewCompanyDialog;
